import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, StatusBadge, LoadingSkeleton, EmptyState, ConfirmDialog } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Truck, Ban, Copy, Check, User, Package, Receipt, Clock, AlertTriangle, MapPin,
  RotateCcw, RefreshCw, Warehouse, MessageSquare, ArrowRight,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const REASON_LABELS = {
  damaged: "Damaged / defective product",
  wrong_item: "Wrong item delivered",
  not_as_described: "Product not as described",
  size_fit: "Size / fit issue",
  quality: "Quality not as expected",
  missing_parts: "Missing parts / accessories",
  no_longer_needed: "No longer needed",
  other: "Other",
};

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <dt className="text-sm text-slate-500 shrink-0">{label}</dt>
    <dd className={`text-sm text-slate-800 text-right break-words ${mono ? "font-mono" : ""}`}>{value ?? "-"}</dd>
  </div>
);

// Reverse-courier picker — fetches /returns/{id}/quote, ships the chosen courier.
const ReturnCourierSelect = ({ returnId, onShipped }) => {
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [shipping, setShipping] = useState(null); // courier_code being shipped

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/b2c/seller/returns/${returnId}/quote`)
      .then((r) => alive && setQuote(r.data))
      .catch((e) => alive && setError(errDetail(e, "Couldn't load reverse couriers")))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [returnId]);

  const ship = async (option) => {
    setShipping(option.courier_code);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/returns/${returnId}/ship`, {
        courier_code: option.courier_code,
        mode: option.mode || "surface",
      });
      toast.success("Reverse pickup booked — the courier will collect from your buyer.");
      onShipped?.(data);
    } catch (e) {
      toast.error(errDetail(e, "Could not book the reverse pickup"));
    } finally {
      setShipping(null);
    }
  };

  if (loading) return <LoadingSkeleton variant="list" />;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const options = quote?.options || [];
  if (!options.length) {
    return (
      <p className="text-sm text-slate-500">
        No courier is available for this return lane yet. Try again shortly.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {quote?.zone && (
        <p className="text-xs text-slate-500">
          Reverse lane zone <span className="font-semibold">{quote.zone}</span> · charged weight{" "}
          {quote.charged_weight_kg} kg
        </p>
      )}
      {options.map((o) => (
        <div
          key={`${o.courier_code}-${o.mode}`}
          className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            {o.logo_url ? (
              <img src={o.logo_url} alt="" className="w-8 h-8 rounded object-contain" />
            ) : (
              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                <Truck className="w-4 h-4 text-slate-400" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{o.name || o.courier_code}</p>
              <p className="text-xs text-slate-500 capitalize">{o.mode || "surface"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-bold text-slate-800 tabular-nums">{inr(o.total)}</span>
            <Button size="sm" disabled={!!shipping} onClick={() => ship(o)}>
              {shipping === o.courier_code ? "Booking…" : "Ship"}
            </Button>
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-400">
        The reverse-shipping charge is debited from your wallet when you book the pickup.
      </p>
    </div>
  );
};

const B2CReturnDetail = ({ user, onLogout }) => {
  const { returnId } = useParams();
  const navigate = useNavigate();

  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShip, setShowShip] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchReturn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/returns/${returnId}`);
      setRet(data);
    } catch (e) {
      setError(errDetail(e, "Could not load this return"));
    } finally {
      setLoading(false);
    }
  }, [returnId]);

  useEffect(() => {
    fetchReturn();
  }, [fetchReturn]);

  const copyAwb = () => {
    if (!ret?.awb) return;
    navigator.clipboard?.writeText(ret.awb).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/returns/${returnId}/cancel`);
      toast.success("Return cancelled — any reverse-shipping charge is auto-refunded.");
      setRet(data);
      setCancelOpen(false);
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel this return"));
    } finally {
      setCancelling(false);
    }
  };

  const canShip = can(user, "b2c.returns:ship");
  const canCancel = can(user, "b2c.returns:cancel");
  const isCancellable = ret && ["REQUESTED", "AWB_ASSIGNED", "PICKUP_SCHEDULED"].includes(ret.status);
  const rate = ret?.seller_rate;

  if (loading) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="space-y-6"><LoadingSkeleton variant="detail" /></div>
      </SellerLayout>
    );
  }

  if (error || !ret) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader title="Return" backTo={() => navigate("/seller/b2c/returns")} />
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={AlertTriangle}
                title="Return not found"
                description={error || "This return doesn't exist or you don't have access to it."}
                action={<Button variant="outline" onClick={() => navigate("/seller/b2c/returns")}>Back to returns</Button>}
              />
            </CardContent>
          </Card>
        </div>
      </SellerLayout>
    );
  }

  const history = Array.isArray(ret.status_history) ? ret.status_history : [];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title={ret.return_number}
          subtitle={`Created ${formatToIST(ret.created_at)}`}
          backTo={() => navigate("/seller/b2c/returns")}
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={ret.status} kind="b2c" size="lg" />
              {ret.type === "exchange" ? (
                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">
                  <RefreshCw className="w-3 h-3 mr-1" /> Exchange
                </Badge>
              ) : (
                <Badge variant="outline" className="border-slate-200 text-slate-700">
                  <RotateCcw className="w-3 h-3 mr-1" /> Return
                </Badge>
              )}
            </div>
          }
          actions={
            <>
              {ret.status === "REQUESTED" && canShip && (
                <Button onClick={() => setShowShip((v) => !v)} data-testid="return-ship-now">
                  <Truck className="w-4 h-4 mr-2" /> {showShip ? "Hide couriers" : "Ship return"}
                </Button>
              )}
              {isCancellable && canCancel && (
                <Button variant="outline" className="text-red-600 hover:bg-red-50"
                  onClick={() => setCancelOpen(true)} data-testid="return-cancel">
                  <Ban className="w-4 h-4 mr-2" /> Cancel
                </Button>
              )}
            </>
          }
        />

        {/* Reverse AWB / courier strip */}
        {(ret.awb || ret.courier_code) && (
          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between py-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {ret.courier_code && (
                  <div>
                    <p className="text-xs text-slate-500">Courier</p>
                    <p className="text-sm font-semibold text-slate-800">{ret.courier_name || ret.courier_code}</p>
                  </div>
                )}
                {ret.awb && (
                  <div>
                    <p className="text-xs text-slate-500">Reverse AWB</p>
                    <button onClick={copyAwb} className="inline-flex items-center gap-1.5 text-sm font-mono font-semibold text-slate-800 hover:text-brand">
                      {ret.awb}
                      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reverse courier select (REQUESTED, Ship return) */}
        {ret.status === "REQUESTED" && showShip && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-brand" /> Choose a courier for the reverse pickup
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ReturnCourierSelect
                returnId={ret.id}
                onShipped={(updated) => {
                  setShowShip(false);
                  if (updated?.id) setRet(updated);
                  else fetchReturn();
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Exchange: linked replacement order */}
        {ret.type === "exchange" && ret.replacement_order_id && (
          <Card className="border-0 shadow-sm border-l-4 border-l-indigo-300">
            <CardContent className="flex items-center justify-between gap-3 py-4">
              <div>
                <p className="text-xs text-slate-500">Replacement forward order</p>
                <p className="text-sm font-semibold text-slate-800">{ret.replacement_order_number}</p>
                <p className="text-xs text-slate-500">Ship the replacement to your buyer from B2C Orders.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/seller/b2c/orders/${ret.replacement_order_id}`)}>
                Open order <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: timeline + reason */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-brand" /> Status timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500">No status updates yet.</p>
                ) : (
                  <ol className="relative border-l border-slate-200 ml-2 space-y-5">
                    {history.map((h, i) => (
                      <li key={i} className="ml-4">
                        <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full ${i === history.length - 1 ? "bg-brand" : "bg-slate-300"}`} />
                        <StatusBadge status={h.status} kind="b2c" size="sm" />
                        {h.at && <p className="mt-0.5 text-xs text-slate-400">{formatToIST(h.at)}</p>}
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-brand" /> Reason
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-slate-800">{REASON_LABELS[ret.reason] || ret.reason}</p>
                {ret.reason_note && <p className="mt-1 text-sm text-slate-600">{ret.reason_note}</p>}
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Pickup QC:</span>
                  {ret.qc?.required ? (
                    <span className="font-medium text-brand">Quality check at doorstep</span>
                  ) : (
                    <span className="text-slate-600">Standard (Non-QC)</span>
                  )}
                </div>
                {ret.qc?.required && (
                  <p className="mt-1 text-xs text-slate-500">
                    {[
                      ret.qc.brand && `Brand: ${ret.qc.brand}`,
                      ret.qc.color && `Colour: ${ret.qc.color}`,
                      ret.qc.serial_no && `Serial: ${ret.qc.serial_no}`,
                      ret.qc.size && `Size: ${ret.qc.size}`,
                      ret.qc.ean_barcode && `EAN: ${ret.qc.ean_barcode}`,
                    ].filter(Boolean).join(" · ") || "The pickup agent verifies the product at the door."}
                  </p>
                )}
                <div className="mt-3 text-xs text-slate-500">
                  Against order{" "}
                  <button className="text-brand hover:underline" onClick={() => navigate(`/seller/b2c/orders/${ret.order_id}`)}>
                    {ret.order_number}
                  </button>
                  {ret.original_awb ? ` · AWB ${ret.original_awb}` : ""}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: pickup (buyer) / destination / items / charges */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-brand" /> Pickup from (buyer)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Name" value={ret.buyer?.name} />
                    <Row label="Phone" value={ret.buyer?.phone} />
                    <Row
                      label="Address"
                      value={
                        <span className="inline-flex items-start gap-1 justify-end">
                          <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                          <span>
                            {ret.buyer?.address}
                            {ret.buyer?.landmark ? `, ${ret.buyer.landmark}` : ""}
                            {", "}
                            {[ret.buyer?.city, ret.buyer?.state, ret.buyer?.pincode].filter(Boolean).join(", ")}
                          </span>
                        </span>
                      }
                    />
                  </dl>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Warehouse className="w-5 h-5 text-brand" /> Return to
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Destination" value="Your pickup location" />
                    <Row label="Zone" value={ret.zone ? <Badge variant="outline" className="border-slate-200">{ret.zone}</Badge> : "-"} />
                    <Row label="Charged weight" value={<span className="font-semibold">{ret.package?.charged_weight_kg ?? "-"} kg</span>} />
                    <Row label="Dimensions" value={`${ret.package?.length_cm ?? "-"}×${ret.package?.breadth_cm ?? "-"}×${ret.package?.height_cm ?? "-"} cm`} />
                  </dl>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-brand" /> Returned items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(ret.products || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">
                        {p.sku ? `SKU ${p.sku} · ` : ""}Qty {p.qty} × {inr(p.unit_price)}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {rate && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-brand" /> Reverse-shipping charges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Freight" value={inr(rate.freight)} />
                    <Row label={`GST${rate.breakdown?.gst_pct ? ` (${rate.breakdown.gst_pct}%)` : ""}`} value={inr(rate.gst)} />
                    <div className="flex items-center justify-between border-t pt-2 mt-1">
                      <dt className="text-sm font-semibold text-slate-700">Wallet debit</dt>
                      <dd className="text-base font-bold text-slate-800 tabular-nums">{inr(rate.total)}</dd>
                    </div>
                  </dl>
                  {ret.refunded_at && (
                    <p className="mt-2 text-xs text-green-700">Refunded to wallet on cancellation.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={`Cancel return ${ret.return_number}?`}
        description="This cancels the reverse pickup before it's collected. If the reverse shipment was already booked, the reverse-shipping charge is automatically refunded."
        confirmLabel="Cancel return"
        tone="destructive"
        loading={cancelling}
        onConfirm={doCancel}
      />
    </SellerLayout>
  );
};

export default B2CReturnDetail;
